import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import axios from "axios";
import { InputField, Spinner, cn, useDebouncedValue } from "@ui";
import type { LocationSuggestion } from "../lib/types";
import { searchLocations } from "../lib/api";

interface Props {
  value: LocationSuggestion | null;
  onChange: (selected: LocationSuggestion | null) => void;
  disabled?: boolean;
}

// Searches the public Location catalog server-side
// (GET /outils/analyse-de-local/locations?q=…). Matches the visual
// language of `Ui::InputAddress` but built in React for direct
// integration with the analysis form.
export function LocationAutocomplete({ value, onChange, disabled }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(query, 250);

  useEffect(() => {
    if (value) return;
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    searchLocations(trimmed, controller.signal)
      .then((items) => {
        setResults(items);
        setOpen(true);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setError("Recherche indisponible. Réessayez.");
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [debouncedQuery, value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-300 bg-white px-3 py-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900 truncate">
            {value.name}
          </div>
          <div className="text-sm text-slate-500 truncate">
            {value.formatted_address ??
              [value.postal_code, value.locality].filter(Boolean).join(" ")}
          </div>
        </div>
        <button
          type="button"
          aria-label="Changer de local"
          onClick={() => {
            onChange(null);
            setQuery("");
            setResults([]);
          }}
          disabled={disabled}
          className="shrink-0 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <InputField
          // Intentionally `text` (not `search`) so WebKit/Blink don't render
          // their native clear-button chrome on top of our custom UI.
          type="text"
          role="searchbox"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Cherchez un local par nom, ville ou quartier…"
          disabled={disabled}
          // `text-base` is critical here: iOS Safari auto-zooms the page
          // when an input with font-size < 16px gets focused. The @ui
          // InputField default is text-sm (14px), so we override locally.
          // `py-2.5` keeps the field 44px-tall, comfortable for thumb taps.
          className="pl-9 py-2.5 text-base"
          aria-label="Recherche de local"
        />
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner size="sm" />
          </span>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {open && results.length > 0 && (
        <ul
          role="listbox"
          className={cn(
            "absolute z-[60] mt-1 max-h-72 w-full overflow-auto rounded-lg",
            "border border-slate-200 bg-white shadow-lg",
          )}
        >
          {results.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => {
                  onChange(item);
                  setOpen(false);
                }}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-3 text-left hover:bg-slate-50"
              >
                <span className="text-base font-medium text-slate-900">
                  {item.name}
                </span>
                <span className="text-sm text-slate-500">
                  {item.formatted_address ??
                    [item.postal_code, item.locality]
                      .filter(Boolean)
                      .join(" ")}
                  {item.surface ? ` · ${item.surface} m²` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && debouncedQuery.length >= 2 && results.length === 0 && !error && (
        <div className="absolute z-[60] mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-500 shadow-lg">
          Aucun local référencé pour « {debouncedQuery} ».
        </div>
      )}
    </div>
  );
}
